import { useAppData } from "@/data/AppDataContext";
import { getStageConfig } from "@/data/workflowEngine";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import {
  ClipboardCheck, FileWarning, Clock, MapPin, ArrowRight, Shield,
  AlertTriangle, Camera, CheckCircle, Calendar, Wrench, ChevronRight,
} from "lucide-react";
import { MyScheduleWidget } from "@/components/MyScheduleWidget";
// import { isApprovedCrew } from "@data/planningReview";
import { isApprovedForCrew } from "@/data/planningReview";

function getJobDisplayRef(job: { id: string; jobNumber?: string | null }): string {
  if (typeof job.jobNumber === "string" && job.jobNumber.trim()) return job.jobNumber.trim();
  return job.id;
}

export function InstallerDashboard() {
  const { jobs, issues } = useAppData();

  const myJobs = jobs.filter(
    (j) => {
      const order = getStageConfig(j.currentStage)?.order ?? 0;
      return j.assignmentType === "Internal Crew" &&
        j.assignedCrew &&
        order >= 70 && order <= 90 && // Assigned through QA
        isApprovedForCrew(j); // P12.4 - hide pending review from crews
    }
  );
  const activeJobs = myJobs.filter((j) => (getStageConfig(j.currentStage)?.order ?? 0) === 80);
  const pendingQA = myJobs.filter((j) => (getStageConfig(j.currentStage)?.order ?? 0) === 90 && !j.qaSubmitted);
  const upcomingJobs = myJobs.filter((j) => (getStageConfig(j.currentStage)?.order ?? 0) === 70);
  const myIssues = issues.filter(
    (i) => myJobs.some((j) => j.id === i.jobId) && (i.status === "Open" || i.status === "In Progress")
  );

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="max-w-2xl mx-auto pb-8 space-y-5">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-bold tracking-tight">Field Ops</h1>
          <p className="text-[11px] text-muted-foreground">{today}</p>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20">
          <Shield className="h-3.5 w-3.5 text-primary" />
          <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Internal Crew</span>
        </div>
      </div>

      {/* ── Compliance banner ── */}
      <div className="rounded-xl border-2 border-primary/20 bg-primary/[0.03] p-3.5">
        <div className="flex items-center gap-2 mb-1.5">
          <Shield className="h-4 w-4 text-primary" />
          <span className="text-xs font-bold text-primary uppercase tracking-wider">QA & Documentation Standard</span>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          All installs require before, progress, and completion photos. The Install QA checklist must be 100% complete before any job can close. No exceptions.
        </p>
      </div>

      {/* ── My Schedule ── */}
      <MyScheduleWidget compact />

      {/* ── Status strip ── */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Active", value: activeJobs.length, icon: Wrench, accent: "text-info", bg: "bg-info/10 border-info/20" },
          { label: "QA Pending", value: pendingQA.length, icon: ClipboardCheck, accent: "text-warning", bg: "bg-warning/10 border-warning/20" },
          { label: "Open Issues", value: myIssues.length, icon: AlertTriangle, accent: "text-destructive", bg: "bg-destructive/10 border-destructive/20" },
        ].map((s) => (
          <div key={s.label} className={`rounded-xl border p-3 text-center ${s.bg}`}>
            <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.accent}`} />
            <p className="text-xl font-bold leading-none">{s.value}</p>
            <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Primary actions ── */}
      <div className="grid grid-cols-2 gap-3">
        {/* P12.2: QA is reached via execution flow only — link to most-urgent job. */}
        {(pendingQA[0] || activeJobs[0] || upcomingJobs[0]) ? (
        <Link 
          // to="/qa-form"
          to={`/installer-execution/${(pendingQA[0] ?? activeJobs[0] ?? upcomingJobs[0]).id}`}
          className="block"
        >
          <button className="w-full py-4 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-transform shadow-md">
            <ClipboardCheck className="h-5 w-5" />
            Open Next Job
          </button>
        </Link>
        ) : (
          <button
            disabled
            className="w-full py-4 rounded-xl bg-muted text-muted-foreground font-bold text-sm flex items-center justify-center gap-2"
          >
            <ClipboardCheck className="h-5 w-5" />
            No Active Jobs
          </button>
        )}
        <Link to="/issue-form" className="block">
          <button className="w-full py-4 rounded-xl bg-destructive/10 border-2 border-destructive/30 text-destructive font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-transform">
            <FileWarning className="h-5 w-5" />
            Escalate Issue
          </button>
        </Link>
      </div>

      {/* ── Active Installs ── */}
      {activeJobs.length > 0 && (
        <section>
          <SectionHeader icon={<Clock className="h-4 w-4 text-info" />} title="Active Installs" count={activeJobs.length} />
          <div className="space-y-2">
            {activeJobs.map((j) => {
              const jobIssues = issues.filter((i) => i.jobId === j.id && i.status === "Open");
              return (
                <JobCard key={j.id} job={j} openIssues={jobIssues.length} showQAStatus />
              );
            })}
          </div>
        </section>
      )}

      {/* ── QA Pending ── */}
      {pendingQA.length > 0 && (
        <section>
          <SectionHeader icon={<ClipboardCheck className="h-4 w-4 text-warning" />} title="QA Required" count={pendingQA.length} urgent />
          <div className="space-y-2">
            {pendingQA.map((j) => (
              <Link 
                key={j.id} 
                // to="/qa-form" 
                to={`/installer-execution/${j.id}`}
                className="block">
                <div className="flex items-center justify-between p-3.5 rounded-xl border-2 border-warning/30 bg-warning/5 active:scale-[0.98] transition-transform">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-warning/20 flex items-center justify-center">
                      <Camera className="h-5 w-5 text-warning" />
                    </div>
                    <div>
                       <p className="text-sm font-semibold">{j.jobNumber || j.id} — {j.customerName}</p>
                      <p className="text-[10px] text-muted-foreground">{j.projectType} · QA checklist incomplete</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-warning" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── Upcoming / Dispatched ── */}
      {upcomingJobs.length > 0 && (
        <section>
          <SectionHeader icon={<Calendar className="h-4 w-4 text-primary" />} title="Upcoming" count={upcomingJobs.length} />
          <div className="space-y-2">
            {upcomingJobs.map((j) => (
              <JobCard key={j.id} job={j} openIssues={0} />
            ))}
          </div>
        </section>
      )}

      {/* ── Open Issues ── */}
      {myIssues.length > 0 && (
        <section>
          <SectionHeader icon={<AlertTriangle className="h-4 w-4 text-destructive" />} title="Open Issues" count={myIssues.length} />
          <div className="space-y-2">
            {myIssues.map((iss) => {
              const job = jobs.find((j) => j.id === iss.jobId);
              return (
                <Link key={iss.id} to={`/installer-execution/${iss.jobId}`} className="block">
                  <div className="flex items-center gap-3 p-3 rounded-xl border hover:border-destructive/30 transition-colors">
                    <div className={`shrink-0 h-2 w-2 rounded-full ${
                      iss.severity === "Urgent" ? "bg-destructive" : iss.severity === "Medium" ? "bg-warning" : "bg-muted-foreground"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{iss.description}</p>
                      <p className="text-[10px] text-muted-foreground">{job?.id} · {iss.severity} · {iss.issueType}</p>
                    </div>
                    <StatusBadge type="severity" value={iss.severity} />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {myJobs.length === 0 && (
        <div className="py-16 text-center">
          <Wrench className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No active assignments</p>
          <p className="text-xs text-muted-foreground mt-1">Check back for new dispatched jobs.</p>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──

function SectionHeader({ icon, title, count, urgent }: { icon: React.ReactNode; title: string; count: number; urgent?: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-1">
      {icon}
      <h2 className="text-sm font-bold tracking-tight">{title}</h2>
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
        urgent ? "bg-warning/20 text-warning" : "bg-muted text-muted-foreground"
      }`}>{count}</span>
    </div>
  );
}

function JobCard({ job, openIssues, showQAStatus }: { job: any; openIssues: number; showQAStatus?: boolean }) {
  const { profile } = useCurrentUser();
  const { counts } = useJobMessageNotifications(job.id, profile?.id);
  const docProgress = job.qaSubmitted ? 100 : job.currentStage === "In Progress" ? 40 : 0;
  return (
    <div className="relative">
      <Link to={`/installer-execution/${job.id}`} className="block">
        <Card className="border hover:border-primary/30 transition-colors active:scale-[0.99]">
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary">{getJobDisplayRef(job)}</span>
                <StatusBadge type="stage" value={job.currentStage} />
              </div>
              {job.scheduledInstallDate && (
                <span className="text-[10px] text-muted-foreground">{job.scheduledInstallDate}</span>
              )}
            </div>
            <p className="text-sm font-semibold">{job.customerName}</p>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
              <MapPin className="h-3 w-3 shrink-0" /> {typeof job.address === "string" ? job.address : `${job.address.street}, ${job.address.city}`}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{job.projectType} · {job.scopeSummary}</p>

            {/* Status row */}
            <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t">
              {showQAStatus && (
                <div className="flex items-center gap-1.5 flex-1">
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Docs</span>
                  <Progress value={docProgress} className="h-1.5 flex-1" />
                  <span className="text-[9px] font-bold text-muted-foreground">{docProgress}%</span>
                </div>
              )}
              {openIssues > 0 && (
                <div className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-destructive" />
                  <span className="text-[10px] font-bold text-destructive">{openIssues} issue{openIssues > 1 ? "s" : ""}</span>
                </div>
              )}
              {job.qaSubmitted && (
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-success" />
                  <span className="text-[10px] font-bold text-success">QA Done</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
      {counts.total > 0 && (
        <div className="absolute -top-1 -right-1 h-[18px] min-w-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center leading-none border-2 border-background">
          {counts.total > 99 ? "99+" : counts.total}
        </div>
      )}
    </div>
  );
}
